/**
 * 2026-09-23 — Destek paneli (tg-support-panel) bağlantı anahtarları.
 *
 * Admin side of contract C3 (capability `chat:integrations`):
 *   GET  /admin/chat/integration-keys             → {keys:[SupportPanelKey]}
 *   POST /admin/chat/integration-keys {name}      → 201 {key, token:'kpsp_…'}
 *   POST /admin/chat/integration-keys/:id/revoke  → 200 {key}
 *
 * The raw token is returned by the server ONLY in the create response; the
 * server keeps a SHA-256 digest and exposes `fingerprint` (first 8 hex of that
 * digest — the same value the panel's "Bağlantıyı test et" shows). This module
 * never logs, caches or persists the token: it is handed straight to the caller,
 * which must keep it only for as long as the one-time reveal dialog is open.
 *
 * Fail loud: a payload that does not look like the contract (missing `keys`,
 * a create answer without `token`) is an error, never an empty list.
 */
import { callAdminApi, type ApiClientOptions, type ApiResult } from './client.js';
import { liftServerCode } from './overview.js';

/** What the operator pastes into the support panel's admin page (with the key). */
export const SUPPORT_PANEL_API_BASE_URL = 'https://api.kopechka.com/integrations/support-chat/v1';
export const SUPPORT_PANEL_KEYS_PATH = '/admin/chat/integration-keys';
export const SUPPORT_PANEL_KEY_NAME_MAX = 60;

export interface SupportPanelKey {
  readonly id: string;
  readonly name: string;
  /** First 8 hex chars of the stored SHA-256 digest; never the token itself. */
  readonly fingerprint: string;
  readonly createdAt: string;
  readonly createdBy: string | null;
  readonly lastUsedAt: string | null;
  readonly revokedAt: string | null;
}

export interface SupportPanelKeyList {
  readonly keys: readonly SupportPanelKey[];
}

export interface CreatedSupportPanelKey {
  readonly key: SupportPanelKey;
  /** `kpsp_…` — shown once; the server cannot show it again. */
  readonly token: string;
}

export interface RevokedSupportPanelKey {
  readonly key: SupportPanelKey;
}

export async function listSupportPanelKeys(
  options: ApiClientOptions,
): Promise<ApiResult<SupportPanelKeyList>> {
  const res = await callAdminApi<unknown>(options, { path: SUPPORT_PANEL_KEYS_PATH });
  if (res.kind !== 'ok') return liftServerCode(res);
  const data = normalizeSupportPanelKeyList(res.data);
  return data
    ? { kind: 'ok', status: res.status, data }
    : { kind: 'error', status: res.status, reason: 'invalid_keys_payload', body: null };
}

export async function createSupportPanelKey(
  options: ApiClientOptions,
  name: string,
): Promise<ApiResult<CreatedSupportPanelKey>> {
  const res = await callAdminApi<unknown>(options, {
    method: 'POST',
    path: SUPPORT_PANEL_KEYS_PATH,
    body: { name },
  });
  if (res.kind !== 'ok') return liftServerCode(res);
  const data = normalizeCreatedSupportPanelKey(res.data);
  return data
    ? { kind: 'ok', status: res.status, data }
    : { kind: 'error', status: res.status, reason: 'invalid_create_payload', body: null };
}

export async function revokeSupportPanelKey(
  options: ApiClientOptions,
  id: string,
): Promise<ApiResult<RevokedSupportPanelKey>> {
  const res = await callAdminApi<unknown>(options, {
    method: 'POST',
    path: `${SUPPORT_PANEL_KEYS_PATH}/${encodeURIComponent(id)}/revoke`,
    body: {},
  });
  if (res.kind !== 'ok') return liftServerCode(res);
  const key = isRec(res.data) ? normalizeSupportPanelKey(res.data.key) : null;
  return key
    ? { kind: 'ok', status: res.status, data: { key } }
    : { kind: 'error', status: res.status, reason: 'invalid_revoke_payload', body: null };
}

/** Client-side mirror of the server rule (1-60 chars after trim). The server stays authoritative. */
export function validateSupportPanelKeyName(raw: string): string | null {
  const name = raw.trim();
  if (name.length === 0) return 'Bir ad girin.';
  if (name.length > SUPPORT_PANEL_KEY_NAME_MAX) {
    return `Ad en fazla ${SUPPORT_PANEL_KEY_NAME_MAX} karakter olabilir.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Normalisers (pure)
// ---------------------------------------------------------------------------

type Rec = Record<string, unknown>;

function isRec(v: unknown): v is Rec {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

export function normalizeSupportPanelKey(raw: unknown): SupportPanelKey | null {
  if (!isRec(raw)) return null;
  const id = typeof raw.id === 'number' && Number.isFinite(raw.id) ? String(raw.id) : strOrNull(raw.id);
  if (!id) return null;
  return {
    id,
    name: strOrNull(raw.name) ?? '—',
    fingerprint: strOrNull(raw.fingerprint) ?? '',
    createdAt: strOrNull(raw.createdAt) ?? '',
    createdBy: strOrNull(raw.createdBy),
    lastUsedAt: strOrNull(raw.lastUsedAt),
    revokedAt: strOrNull(raw.revokedAt),
  };
}

export function normalizeSupportPanelKeyList(raw: unknown): SupportPanelKeyList | null {
  if (!isRec(raw) || !Array.isArray(raw.keys)) return null;
  return {
    keys: raw.keys
      .map(normalizeSupportPanelKey)
      .filter((k): k is SupportPanelKey => k !== null),
  };
}

export function normalizeCreatedSupportPanelKey(raw: unknown): CreatedSupportPanelKey | null {
  if (!isRec(raw)) return null;
  const token = strOrNull(raw.token);
  const key = normalizeSupportPanelKey(raw.key);
  if (!token || !key) return null;
  return { key, token };
}

/** Active keys first, then newest first — stable for equal timestamps. */
export function sortSupportPanelKeys(keys: readonly SupportPanelKey[]): SupportPanelKey[] {
  return [...keys].sort((a, b) => {
    const ra = a.revokedAt === null ? 0 : 1;
    const rb = b.revokedAt === null ? 0 : 1;
    if (ra !== rb) return ra - rb;
    return a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0;
  });
}
